import { forwardRef } from 'react';
import { ChatMessage } from './ChatMessage';
import { TypingIndicator } from './TypingIndicator';
import { ChatErrorState, ChatLoadingState, ChatEmptyState } from './ChatStates';
import type { Message } from '@/types';

interface ChatContentProps {
  readonly messages: readonly Message[];
  readonly chatState: {
    readonly isLoading: boolean;
    readonly isTyping: boolean;
    readonly error: string | null;
    readonly errorDetails?: {
      readonly title?: string;
      readonly detail?: string;
      readonly traceId?: string;
      readonly instance?: string;
    };
    readonly chatId: number | null;
  };
  readonly onRetryMessage?: () => void;
  readonly onClearError?: () => void;
  readonly isRetrying?: boolean;
}

export const ChatContent = forwardRef<HTMLDivElement, ChatContentProps>(
  ({ messages, chatState, onRetryMessage, onClearError, isRetrying }, ref) => {
    const { isLoading, isTyping, error, errorDetails, chatId } = chatState;

    // Early returns for different states
    if (error) {
      return (
        <ChatErrorState 
          error={error} 
          errorDetails={errorDetails}
          firstMessage={messages[0]?.content || ''} 
          onRetry={onRetryMessage}
          onClearError={onClearError}
          isRetrying={isRetrying}
        />
      );
    }

    if (isLoading) {
      return <ChatLoadingState />;
    }

    if (messages.length === 0) {
      return <ChatEmptyState chatId={chatId} />;
    }

    // Main messages view
    return (
      <div className="w-full">
        <div className="space-y-4">
          {messages.map((message) => (
            <ChatMessage 
              key={message.id} 
              message={message}
            />
          ))}
          {isTyping && <TypingIndicator />}
        </div>
        <div ref={ref} />
      </div>
    );
  }
);

ChatContent.displayName = 'ChatContent';
